import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from './SEOHead';
import Nav from './Nav';
import Footer from './Footer';
import '../assets/css/legal.css';

const TermsConditions = () => {
  const { lang } = useParams();
  const { language, setLanguage } = useLanguage();

  // Set language based on URL parameter
  React.useEffect(() => {
    if (lang && lang !== language) {
      setLanguage(lang);
    }
  }, [lang, language, setLanguage]);

  const seoContent = {
    en: {
      title: "Terms of Service - Audiosum AI Transcription",
      description: "Read Audiosum's terms of service. Understand user rights, acceptable use, payment terms, and legal obligations for our AI transcription platform.",
      keywords: "terms of service, user agreement, legal terms, audiosum terms, service conditions"
    },
    ar: {
      title: "شروط الخدمة - لخص تحويل الصوت بالذكاء الاصطناعي",
      description: "اقرأ شروط خدمة لخص. تعرف على حقوق المستخدم والاستخدام المقبول وشروط الدفع والالتزامات القانونية لمنصة التحويل بالذكاء الاصطناعي.",
      keywords: "شروط الخدمة, اتفاقية المستخدم, شروط قانونية, شروط لخص, شروط الخدمة"
    }
  };

  const currentSEO = seoContent[language] || seoContent.en;

  const getContent = () => {
    if (language === 'ar') {
      return (
        <div className="terms-content" dir="rtl">
          <h1>شروط الخدمة</h1>
          
          <div className="important-notice">
            <h2>مهم - يرجى قراءة هذه الشروط بعناية</h2>
            <p>
              تشكل شروط الخدمة هذه ("الشروط"، "الاتفاقية") اتفاقية ملزمة قانونياً بينك ("المستخدم"، "أنت"، "الخاص بك") وبين Audiosum ("الشركة"، "نحن"، "لنا"، "خاصتنا")، تحكم وصولك واستخدامك لخدمات النسخ والتلخيص والتحليل المدعومة بالذكاء الاصطناعي للملفات الصوتية والمرئية من Audiosum، والمتاحة من خلال منصاتنا الإلكترونية audiosum.app و audiosum.ai، والتطبيقات المحمولة، وواجهات البرمجة، والخدمات ذات الصلة (مجتمعة، "الخدمة" أو "المنصة").
            </p>
            <p>
              بالوصول إلى أو تحميل أو تثبيت أو استخدام أي جزء من الخدمة، فإنك تقر بأنك قد قرأت وفهمت ووافقت على الالتزام بهذه الشروط وجميع القوانين واللوائح المعمول بها. إذا كنت لا توافق على أي بند من هذه الشروط، يجب عليك التوقف فوراً عن استخدام الخدمة.
            </p>
          </div>

          <section>
            <h2>1. قبول الشروط وتعديلها</h2>
            <h3>1.1 الاتفاقية الملزمة</h3>
            <p>
              يشكل وصولك إلى الخدمة أو استخدامها قبولك لهذه الشروط وسياسة الخصوصية الخاصة بنا (المدمجة هنا بالإشارة)، وأي شروط إضافية قابلة للتطبيق على ميزات أو خدمات محددة. تصبح هذه الاتفاقية سارية المفعول فور أول استخدام لك للخدمة.
            </p>
            
            <h3>1.2 تعديل الشروط</h3>
            <p>نحتفظ بالحق في تعديل أو تحديث أو استبدال هذه الشروط في أي وقت وفقاً لتقديرنا المطلق. سيتم إبلاغ التغييرات المهمة من خلال:</p>
            <ul>
              <li>إشعار بالبريد الإلكتروني للمستخدمين المسجلين (إذا أمكن)</li>
              <li>إشعار بارز على موقعنا الإلكتروني</li>
              <li>إشعارات داخل التطبيق</li>
              <li>تاريخ "آخر تعديل" محدث في هذه الوثيقة</li>
            </ul>
            <p>يشكل استمرار استخدامك للخدمة بعد أي تغييرات قبولك للشروط المعدلة. من مسؤوليتك مراجعة هذه الشروط بشكل دوري للاطلاع على التحديثات.</p>

            <h3>1.3 الشروط الإضافية</h3>
            <p>قد تخضع ميزات أو خدمات معينة لشروط وأحكام إضافية، والتي ستُعرض عليك وقت الوصول. تُدمج هذه الشروط الإضافية في هذه الاتفاقية بالإشارة.</p>
          </section>

          <section>
            <h2>2. الأهلية وتسجيل الحساب</h2>
            <h3>2.1 متطلبات العمر</h3>
            <p>
              الخدمة مخصصة للمستخدمين الذين لا تقل أعمارهم عن 13 عاماً. إذا كنت بين 13 و 18 عاماً (أو سن الرشد في اختصاصك القضائي)، فقد تستخدم الخدمة فقط بمشاركة وموافقة ولي الأمر أو الوصي القانوني. يُمنع بشكل صارم المستخدمون الأقل من 13 عاماً من استخدام الخدمة.
            </p>
            
            <h3>2.2 تسجيل الحساب</h3>
            <ul>
              <li>إنشاء الحساب من خلال البريد الإلكتروني أو Google OAuth أو Apple Sign-In اختياري ولكنه مطلوب لتتبع حدود الاستخدام وإدارة الاشتراكات والوصول إلى ميزات معينة</li>
              <li>يجب عليك تقديم معلومات دقيقة وحالية وكاملة أثناء التسجيل</li>
              <li>أنت مسؤول وحدك عن الحفاظ على سرية وأمان بيانات اعتماد حسابك</li>
              <li>يجب عليك إخطارنا فوراً بأي وصول غير مصرح به أو انتهاك أمني</li>
              <li>أنت مسؤول بالكامل عن جميع الأنشطة التي تحدث تحت حسابك</li>
            </ul>

            <h3>2.3 حقوق إنهاء الحساب</h3>
            <p>نحتفظ بالحق في تعليق أو تعطيل أو إنهاء حسابك في أي وقت لـ:</p>
            <ul>
              <li>انتهاك هذه الشروط</li>
              <li>نشاط مشتبه به احتيالي أو مسيء أو غير قانوني</li>
              <li>فترات طويلة من عدم النشاط</li>
              <li>أسباب تقنية أو أمنية</li>
              <li>عدم دفع الرسوم (للحسابات المدفوعة)</li>
            </ul>
          </section>

          <section>
            <h2>3. منح الترخيص والاستخدام المسموح</h2>
            <h3>3.1 منح الترخيص</h3>
            <p>بشرط امتثالك لهذه الشروط، تمنحك Audiosum ترخيصاً محدوداً وغير حصري وغير قابل للنقل وغير قابل للترخيص الفرعي وقابل للإلغاء للوصول إلى الخدمة واستخدامها لأغراضك الشخصية أو غير التجارية أو التجارية الداخلية وفقاً لهذه الشروط.</p>

            <h3>3.2 الاستخدامات المسموحة</h3>
            <p>يمكنك استخدام الخدمة لـ:</p>
            <ul>
              <li>رفع ومعالجة الملفات الصوتية والمرئية للنسخ والتلخيص</li>
              <li>إنشاء ملخصات وتحليلات مدعومة بالذكاء الاصطناعي لمحتواك</li>
              <li>الوصول إلى وتحميل المحتوى والملخصات المعالجة الخاصة بك</li>
              <li>استخدام واجهة البرمجة الخاصة بنا (إن أمكن) وفقاً لشروط واجهة البرمجة المنفصلة</li>
              <li>التكامل مع تطبيقات الطرف الثالث كما هو مسموح صراحة</li>
            </ul>

            <h3>3.3 الأنشطة المحظورة</h3>
            <p>توافق صراحة على عدم القيام بما يلي:</p>
            
            <h4>3.3.1 القيود التقنية</h4>
            <ul>
              <li>الهندسة العكسية أو إلغاء التجميع أو التفكيك أو محاولة اشتقاق الكود المصدري للخدمة</li>
              <li>استخدام نصوص برمجية آلية أو روبوتات أو وسائل آلية أخرى للوصول إلى الخدمة</li>
              <li>محاولة الحصول على وصول غير مصرح به إلى أنظمتنا أو شبكاتنا أو حسابات المستخدمين</li>
              <li>التدخل في أو تعطيل تشغيل الخدمة أو الخوادم أو الشبكات</li>
              <li>تجاوز أي تدابير أمنية أو ضوابط الوصول</li>
              <li>استخدام الخدمة لتطوير منتجات أو خدمات منافسة</li>
            </ul>

            <h4>3.3.2 قيود المحتوى</h4>
            <ul>
              <li>رفع محتوى ينتهك حقوق الملكية الفكرية للأطراف الثالثة</li>
              <li>رفع محتوى يحتوي على برامج ضارة أو فيروسات أو كود خبيث</li>
              <li>رفع محتوى غير قانوني أو ضار أو مهدد أو مسيء أو مرفوض</li>
              <li>رفع محتوى يحتوي على معلومات شخصية محددة للأطراف الثالثة دون موافقة</li>
              <li>رفع مواد محمية بحقوق الطبع والنشر دون إذن مناسب</li>
              <li>رفع محتوى ينتهك قوانين أو لوائح مراقبة التصدير</li>
            </ul>

            <h4>3.3.3 قيود الاستخدام</h4>
            <ul>
              <li>تجاوز حدود الاستخدام أو الحصص المخصصة لحسابك</li>
              <li>استخدام الخدمة لأنشطة غير قانونية أو احتيالية أو ضارة</li>
              <li>إعادة بيع أو إعادة توزيع أو ترخيص فرعي للخدمة دون إذن كتابي صريح</li>
              <li>استخدام الخدمة للمنافسة مع Audiosum أو تطوير خدمات مماثلة</li>
              <li>انتهاك أي قوانين أو لوائح معمول بها فيما يتعلق باستخدامك</li>
            </ul>
          </section>

          <section>
            <h2>4. محتوى المستخدم والملكية الفكرية</h2>
            <h3>4.1 ملكية محتواك</h3>
            <p>تحتفظ بجميع الحقوق والملكية والمصلحة في أي محتوى صوتي أو مرئي أو محتوى آخر تقوم برفعه إلى الخدمة ("محتوى المستخدم"). نحن لا ندعي ملكية محتوى المستخدم الخاص بك.</p>

            <h3>4.2 ترخيص معالجة المحتوى</h3>
            <p>برفع محتوى المستخدم، تمنح Audiosum ترخيصاً عالمياً وغير حصري وخالٍ من حقوق الملكية لـ:</p>
            <ul>
              <li>الوصول إلى وتخزين ومعالجة محتوى المستخدم الخاص بك فقط لتوفير الخدمة</li>
              <li>إنشاء نسخ وملخصات وتحليلات بناءً على محتوى المستخدم الخاص بك</li>
              <li>تخزين النتائج المعالجة مؤقتاً</li>
              <li>استخدام البيانات المجمعة والمجهولة لتحسين الخدمة (دون معلومات شخصية محددة)</li>
            </ul>

            <h3>4.3 مسؤوليات المحتوى</h3>
            <p>أنت تؤكد وتضمن أن:</p>
            <ul>
              <li>تملك أو لديك جميع الحقوق اللازمة لرفع ومعالجة محتوى المستخدم الخاص بك</li>
              <li>محتوى المستخدم الخاص بك لا ينتهك أي حقوق للأطراف الثالثة أو القوانين المعمول بها</li>
              <li>حصلت على جميع الموافقات اللازمة لمعالجة البيانات الشخصية في محتوى المستخدم الخاص بك</li>
              <li>استخدامك للخدمة يتوافق مع جميع قوانين الخصوصية وحماية البيانات المعمول بها</li>
            </ul>

            <h3>4.4 إزالة المحتوى</h3>
            <p>نحتفظ بالحق في إزالة أو تعطيل الوصول إلى أي محتوى مستخدم:</p>
            <ul>
              <li>ينتهك هذه الشروط</li>
              <li>ينتهك حقوق الأطراف الثالثة</li>
              <li>يُبلغ عنه كغير مناسب أو غير قانوني</li>
              <li>يشكل مخاطر أمنية أو تقنية على خدمتنا</li>
            </ul>

            <h3>4.5 الملكية الفكرية لـ Audiosum</h3>
            <p>الخدمة، بما في ذلك جميع البرامج والخوارزميات والتصاميم والنصوص والرسوم والشعارات والمواد الأخرى، مملوكة لـ Audiosum ومحمية بقوانين الملكية الفكرية. لا يمكنك استخدام ملكيتنا الفكرية إلا كما هو مسموح صراحة في هذه الشروط.</p>
          </section>

          <section>
            <h2>5. خطط الاشتراك والفوترة والمدفوعات</h2>
            <h3>5.1 مستويات الخدمة</h3>
            <p>تعمل Audiosum على نموذج مجاني جزئي مع المستويات التالية:</p>
            <ul>
              <li>التجربة المجانية: فترة تجربة مجانية لمدة 7 أيام؛ يُشترط وجود طريقة دفع صالحة عند التسجيل. بعد انتهاء التجربة، يتم ترقية حسابك تلقائياً إلى خطة Starter بسعر 6.99$/شهر</li>
              <li>خطة Pro: 14.99 دولار/شهر (فوترة شهرية) مع ميزات محسنة وحدود استخدام أعلى</li>
              <li>خطة Premium: 28.99 دولار/شهر (فوترة شهرية) مع ميزات متقدمة وحدود استخدام قصوى</li>
              <li>الخطط السنوية: متاحة مع خصم 17% مقارنة بالفوترة الشهرية</li>
            </ul>

            <h3>5.2 شروط الفوترة</h3>
            <ul>
              <li>يتم تحصيل جميع الرسوم مقدماً على أساس متكرر (شهري أو سنوي)</li>
              <li>تبدأ دورات الفوترة في تاريخ شراء اشتراكك الأولي</li>
              <li>التجديد التلقائي مفعل بشكل افتراضي؛ يمكنك تعطيله في إعدادات حسابك</li>
              <li>سيتم إبلاغ تغييرات الأسعار قبل 30 يوماً للمشتركين الحاليين</li>
              <li>تُعالج جميع المدفوعات بالدولار الأمريكي ما لم يُنص على خلاف ذلك</li>
            </ul>

            <h3>5.3 معالجة المدفوعات</h3>
            <ul>
              <li>تُعالج المدفوعات من خلال Stripe، معالج دفع طرف ثالث متوافق مع PCI DSS</li>
              <li>يجب عليك تقديم معلومات دفع صالحة وحالية</li>
              <li>تفوضنا بتحصيل رسوم من طريقة الدفع المحددة لجميع الرسوم المعمول بها</li>
              <li>قد تؤدي المدفوعات الفاشلة إلى تعليق الخدمة بعد فترة سماح</li>
              <li>نحن غير مسؤولين عن أي رسوم أو تكاليف تفرضها مؤسستك المالية</li>
            </ul>

            <h3>5.4 حدود الاستخدام والتجاوزات</h3>
            <ul>
              <li>تتضمن كل خطة حدود استخدام محددة (وقت المعالجة، حجم الملف، عدد الملفات)</li>
              <li>تجاوز حدود الاستخدام قد يؤدي إلى رسوم إضافية أو قيود خدمة</li>
              <li>يعود الاستخدام إلى الصفر في بداية كل دورة فوترة</li>
              <li>خطط المؤسسات مع حدود استخدام مخصصة متاحة عند الطلب</li>
            </ul>

            <h3>5.5 سياسة الاسترداد والإلغاء</h3>
            <h4>5.5.1 الطبيعة غير القابلة للاسترداد</h4>
            <p>جميع رسوم الاشتراك غير قابلة للاسترداد إلا كما هو منصوص عليه صراحة أدناه. تنطبق سياسة عدم الاسترداد هذه بغض النظر عما إذا كنت تستخدم الخدمة أثناء فترة اشتراكك.</p>

            <h4>5.5.2 حقوق الإلغاء</h4>
            <ul>
              <li>يمكنك إلغاء اشتراكك في أي وقت من خلال إعدادات حسابك</li>
              <li>تصبح عمليات الإلغاء سارية المفعول في نهاية فترة الفوترة الحالية</li>
              <li>ستحتفظ بالوصول إلى الميزات المدفوعة حتى نهاية فترتك المدفوعة</li>
              <li>لا تُقدم استردادات جزئية للأجزاء غير المستخدمة من فترات الفوترة</li>
              <li>عند الإلغاء، سينتقل حسابك تلقائياً إلى المستوى المجاني</li>
            </ul>

            <h4>5.5.3 استثناءات الاسترداد المحدودة</h4>
            <p>قد يُنظر في الاستردادات فقط وفقاً لتقديرنا لـ:</p>
            <ul>
              <li>أخطاء الفوترة أو الرسوم المكررة</li>
              <li>المشاكل التقنية التي تمنع الوصول للخدمة لأكثر من 48 ساعة متتالية</li>
              <li>الرسوم الاحتيالية أو غير المصرح بها</li>
              <li>انتهاكات قوانين حماية المستهلك المعمول بها</li>
            </ul>

            <h4>5.5.4 عملية طلب الاسترداد</h4>
            <ul>
              <li>يجب تقديم جميع طلبات الاسترداد في غضون 48 ساعة من مشكلة الفوترة</li>
              <li>يجب إرسال الطلبات إلى support@audiosum.app مع شرح مفصل</li>
              <li>نحتفظ بالحق في التحقيق والتحقق من مطالبات الاسترداد</li>
              <li>قد تستغرق معالجة الاستردادات المعتمدة 5-10 أيام عمل</li>
            </ul>

            <h3>5.6 تغييرات الأسعار</h3>
            <p>نحتفظ بالحق في تعديل أسعار الاشتراك مع إشعار مسبق لمدة 30 يوماً. لن تؤثر تغييرات الأسعار على المشتركين الحاليين حتى فترة التجديد التالية.</p>
          </section>

          <section>
            <h2>6. أمان البيانات والخصوصية</h2>
            <h3>6.1 التزام حماية البيانات</h3>
            <p>نطبق تدابير أمنية بمعايير الصناعة لحماية بياناتك، بما في ذلك التشفير أثناء النقل وفي حالة الراحة ومراكز البيانات الآمنة وعمليات التدقيق الأمني المنتظمة.</p>

            <h3>6.2 تكامل سياسة الخصوصية</h3>
            <p>خصوصيتك مهمة بالنسبة لنا. يحكم جمع واستخدام وحماية معلوماتك الشخصية سياسة الخصوصية الخاصة بنا، والتي تُدمج في هذه الشروط بالإشارة.</p>

            <h3>6.3 موقع معالجة البيانات</h3>
            <p>قد تُعالج وتُخزن بياناتك في مواقع مختلفة حول العالم، بما في ذلك داخل المنطقة الاقتصادية الأوروبية (EEA)، مع وجود ضمانات مناسبة.</p>
          </section>

          <section>
            <h2>7. توفر الخدمة والتعديلات</h2>
            <h3>7.1 توفر الخدمة</h3>
            <p>بينما نسعى للحفاظ على توفر الخدمة المستمر، لا نضمن الوصول دون انقطاع. قد تواجه الخدمة توقفاً للصيانة أو التحديثات أو المشاكل التقنية غير المتوقعة.</p>

            <h3>7.2 تعديلات الخدمة</h3>
            <p>نحتفظ بالحق في تعديل أو تحديث أو إيقاف أي جانب من جوانب الخدمة في أي وقت، بما في ذلك الميزات والوظائف أو المتطلبات التقنية. سنقدم إشعاراً معقولاً للتغييرات المهمة عند الإمكان.</p>

            <h3>7.3 نوافذ الصيانة</h3>
            <p>ستُبلغ الصيانة المجدولة مسبقاً عند الإمكان. قد تحدث الصيانة الطارئة دون إشعار مسبق.</p>
          </section>

          <section>
            <h2>8. الإنهاء</h2>
            <h3>8.1 الإنهاء من قبلك</h3>
            <p>يمكنك إنهاء هذه الاتفاقية في أي وقت من خلال:</p>
            <ul>
              <li>إلغاء اشتراكك (إن أمكن)</li>
              <li>التوقف عن استخدام الخدمة</li>
              <li>طلب حذف الحساب من خلال support@audiosum.app</li>
            </ul>

            <h3>8.2 الإنهاء من قبل Audiosum</h3>
            <p>قد ننهي أو نعلق وصولك فوراً، دون إشعار مسبق، لـ:</p>
            <ul>
              <li>انتهاك جوهري لهذه الشروط</li>
              <li>انتهاك القوانين أو اللوائح المعمول بها</li>
              <li>نشاط مشتبه به احتيالي أو ضار أو غير قانوني</li>
              <li>عدم دفع الرسوم (بعد فترة السماح)</li>
              <li>عدم نشاط طويل (الحسابات المجانية)</li>
              <li>اعتبارات تقنية أو أمنية</li>
            </ul>

            <h3>8.3 آثار الإنهاء</h3>
            <p>عند الإنهاء:</p>
            <ul>
              <li>يتوقف حقك في الوصول إلى واستخدام الخدمة فوراً</li>
              <li>قد نحذف حسابك والبيانات المرتبطة بعد فترة احتفاظ معقولة</li>
              <li>تبقى التزامات الدفع المستحقة مستحقة وواجبة الدفع</li>
              <li>تستمر الأحكام التي بطبيعتها يجب أن تبقى بعد الإنهاء</li>
            </ul>
          </section>

          <section>
            <h2>9. إخلاء المسؤولية والضمانات</h2>
            <h3>9.1 توفير الخدمة "كما هي"</h3>
            <p>تُقدم الخدمة على أساس "كما هي" و "كما متاحة" دون ضمانات من أي نوع، سواء كانت صريحة أو ضمنية أو قانونية أو غير ذلك.</p>

            <h3>9.2 الضمانات المُخلاة</h3>
            <p>إلى أقصى حد يسمح به القانون المعمول به، نخلي مسؤوليتنا عن جميع الضمانات، بما في ذلك:</p>
            <ul>
              <li>القابلية للبيع والملاءمة لغرض معين</li>
              <li>عدم انتهاك حقوق الأطراف الثالثة</li>
              <li>دقة أو اكتمال أو موثوقية نتائج الخدمة</li>
              <li>التشغيل دون انقطاع أو خالٍ من الأخطاء</li>
              <li>أمان نقل أو تخزين البيانات</li>
              <li>التوافق مع أنظمتك أو متطلباتك</li>
            </ul>

            <h3>9.3 قيود تقنية الذكاء الاصطناعي</h3>
            <p>تقر بأن الميزات المدعومة بالذكاء الاصطناعي قد تنتج نتائج غير دقيقة أو غير كاملة أو غير مناسبة. أنت مسؤول وحدك عن مراجعة والتحقق من جميع مخرجات الخدمة قبل الاعتماد عليها.</p>
          </section>

          <section>
            <h2>10. تحديد المسؤولية</h2>
            <h3>10.1 تحديد الأضرار</h3>
            <p>إلى أقصى حد يسمح به القانون، لا تتجاوز المسؤولية الإجمالية لـ Audiosum الناشئة عن أو المتعلقة بهذه الشروط أو الخدمة:</p>
            <ul>
              <li>إجمالي المبلغ المدفوع من قبلك إلى Audiosum في الـ 12 شهراً السابقة للمطالبة</li>
              <li>مائة دولار أمريكي (100 دولار)</li>
            </ul>

            <h3>10.2 الأضرار المستبعدة</h3>
            <p>لن تكون Audiosum مسؤولة في أي حال عن:</p>
            <ul>
              <li>الأضرار غير المباشرة أو العارضة أو الخاصة أو النموذجية أو التبعية</li>
              <li>فقدان الأرباح أو الإيرادات أو البيانات أو الاستخدام أو السمعة أو الخسائر غير الملموسة الأخرى</li>
              <li>الأضرار الناتجة عن الوصول غير المصرح به إلى أو تغيير بياناتك</li>
              <li>الأضرار الناتجة عن سلوك أو محتوى الأطراف الثالثة</li>
              <li>انقطاع الأعمال أو فشل النظام</li>
            </ul>

            <h3>10.3 الغرض الأساسي</h3>
            <p>تنطبق هذه القيود حتى لو تم إخطار Audiosum بإمكانية حدوث أضرار وبغض النظر عن فشل أي غرض أساسي لأي علاج محدود.</p>
          </section>

          <section>
            <h2>11. التعويض</h2>
            <h3>11.1 التزامات التعويض الخاصة بك</h3>
            <p>توافق على الدفاع عن وتعويض وحماية Audiosum وشركاتها التابعة ومسؤوليها ومديريها وموظفيها ووكلائها ومرخصيها من أي مطالبات وأضرار وخسائر وتكاليف ونفقات (بما في ذلك أتعاب المحاماة المعقولة) الناشئة عن أو المتعلقة بـ:</p>
            <ul>
              <li>استخدامك أو سوء استخدامك للخدمة</li>
              <li>محتوى المستخدم الخاص بك أو معالجته</li>
              <li>انتهاكك لهذه الشروط أو القوانين المعمول بها</li>
              <li>انتهاكك لحقوق الأطراف الثالثة</li>
              <li>أي معلومات خاطئة أو مضللة قدمتها</li>
            </ul>

            <h3>11.2 عملية التعويض</h3>
            <p>سنقدم إشعاراً فورياً بأي مطالبة خاضعة للتعويض ونتعاون في الدفاع، بشرط أن فشلنا في تقديم إشعار فوري لن يعفيك من التزامات التعويض إلا بالقدر المتضرر بذلك.</p>
          </section>

          <section>
            <h2>12. القانون الحاكم وحل النزاعات</h2>
            <h3>12.1 القانون الحاكم</h3>
            <p>تخضع هذه الشروط وأي نزاعات تنشأ بموجبها وتُفسر وفقاً لقوانين البرتغال والاتحاد الأوروبي، دون اعتبار لمبادئ تنازع القوانين.</p>

            <h3>12.2 الولاية القضائية والمكان</h3>
            <p>يجب رفع أي إجراء قانوني أو دعوى تتعلق بهذه الشروط في المحاكم المختصة في لشبونة، البرتغال. توافق على ولاية هذه المحاكم وتتنازل عن أي اعتراض على المكان فيها.</p>

            <h3>12.3 حل النزاعات البديل</h3>
            <p>قبل بدء إجراءات قانونية رسمية، توافق الأطراف على محاولة حل النزاعات من خلال المفاوضات بحسن نية. إذا لم تنجح، قد تُحل النزاعات من خلال التحكيم الملزم الذي تديره مؤسسة تحكيم معترف بها، ويُجرى باللغة الإنجليزية أو البرتغالية.</p>

            <h3>12.4 تنازل عن الدعوى الجماعية</h3>
            <p>توافق على أن أي إجراءات حل نزاع ستُجرى على أساس فردي وليس في دعوى جماعية أو موحدة أو تمثيلية.</p>
          </section>

          <section>
            <h2>13. القوة القاهرة</h2>
            <p>لا يكون أي طرف مسؤولاً عن أي تأخير أو فشل في الأداء بسبب أحداث خارجة عن سيطرته المعقولة، بما في ذلك على سبيل المثال لا الحصر:</p>
            <ul>
              <li>الكوارث الطبيعية أو الأوبئة أو أعمال الطبيعة</li>
              <li>إجراءات الحكومة أو القوانين أو اللوائح</li>
              <li>الحرب أو الإرهاب أو الاضطرابات المدنية</li>
              <li>فشل الإنترنت أو الاتصالات</li>
              <li>انقطاع مقدمي الخدمة من الأطراف الثالثة</li>
              <li>الهجمات السيبرانية أو انتهاكات الأمان</li>
            </ul>
            <p>يجب على الطرف المتأثر تقديم إشعار فوري وبذل جهود معقولة لعلاج الوضع.</p>
          </section>

          <section>
            <h2>14. الامتثال والمتطلبات القانونية</h2>
            <h3>14.1 ضوابط التصدير</h3>
            <p>توافق على الامتثال لجميع قوانين ولوائح التصدير والاستيراد المعمول بها. تؤكد أنك لست موجوداً في بلد خاضع للحظر أو مصنف كبلد "داعم للإرهاب".</p>

            <h3>14.2 الامتثال التنظيمي</h3>
            <p>أنت مسؤول عن ضمان امتثال استخدامك للخدمة لجميع القوانين واللوائح ومعايير الصناعة المعمول بها في اختصاصك القضائي.</p>

            <h3>14.3 GDPR وحماية البيانات</h3>
            <p>إذا كنت موجوداً في الاتحاد الأوروبي، فقد تنطبق حقوق وحماية إضافية تحت اللائحة العامة لحماية البيانات (GDPR) كما هو موضح في سياسة الخصوصية الخاصة بنا.</p>
          </section>

          <section>
            <h2>15. الأحكام العامة</h2>
            <h3>15.1 الاتفاقية الكاملة</h3>
            <p>تشكل هذه الشروط، جنباً إلى جنب مع سياسة الخصوصية الخاصة بنا وأي شروط إضافية مشار إليها هنا، الاتفاقية الكاملة بينك وبين Audiosum فيما يتعلق بالخدمة وتحل محل جميع الاتصالات السابقة أو المعاصرة.</p>

            <h3>15.2 قابلية الفصل</h3>
            <p>إذا وُجد أن أي حكم من هذه الشروط غير قابل للتنفيذ أو غير صالح، فسيكون ذلك الحكم محدوداً أو مُلغى إلى أدنى حد ضروري بحيث تبقى الشروط المتبقية سارية المفعول بالكامل.</p>

            <h3>15.3 عدم التنازل</h3>
            <p>عدم إنفاذنا لأي حكم من هذه الشروط لن يُعتبر تنازلاً عن ذلك الحكم أو حقنا في إنفاذه في المستقبل.</p>

            <h3>15.4 التنازل</h3>
            <p>لا يمكنك التنازل عن أو نقل هذه الشروط أو حقوقك بموجبها دون موافقتنا الكتابية المسبقة. قد ننازل عن هذه الشروط دون قيود.</p>

            <h3>15.5 البقاء</h3>
            <p>الأحكام التي بطبيعتها يجب أن تبقى بعد إنهاء هذه الشروط ستبقى، بما في ذلك على سبيل المثال لا الحصر حقوق الملكية الفكرية وإخلاء المسؤولية وتحديد المسؤولية والتزامات التعويض.</p>

            <h3>15.6 اللغة</h3>
            <p>هذه الشروط مكتوبة باللغة الإنجليزية. أي إصدارات مترجمة مقدمة للراحة فقط، والإصدار الإنجليزي يسود في حالة التعارض.</p>

            <h3>15.7 التحديثات والإشعارات</h3>
            <p>قد نقدم إشعارات وتحديثات من خلال البريد الإلكتروني أو إشعارات داخل التطبيق أو بالنشر على موقعنا الإلكتروني. من مسؤوليتك الحفاظ على معلومات الاتصال الحالية.</p>
          </section>

          <section className="contact-section">
            <h2>16. معلومات الاتصال</h2>
            <p>للأسئلة أو المخاوف أو الدعم فيما يتعلق بشروط الخدمة هذه، يرجى الاتصال بنا:</p>
            <p>البريد الإلكتروني: <a href="mailto:support@audiosum.app">support@audiosum.app</a></p>
            <p>الموقع الإلكتروني: <a href="https://audiosum.app">https://audiosum.app</a></p>
            <p>الإدارة القانونية: فريق Audiosum القانوني</p>
            <p>وقت الاستجابة: نهدف للرد على جميع الاستفسارات في غضون 48 ساعة خلال أيام العمل.</p>
          </section>

          <footer className="terms-footer">
            <p>باستخدام خدمة Audiosum، تقر بأنك قد قرأت وفهمت ووافقت على الالتزام بشروط الخدمة هذه.</p>
            <p>آخر تحديث: 24 سبتمبر، 2025</p>
            <p>© 2025 Audiosum. جميع الحقوق محفوظة.</p>
          </footer>
        </div>
      );
    }

    return (
      <div className="terms-content">
        <h1>Terms of Service</h1>
        
        <div className="important-notice">
          <h2>IMPORTANT - PLEASE READ THESE TERMS CAREFULLY</h2>
          <p>
            These Terms of Service ("Terms," "Agreement") constitute a legally binding agreement between you ("User," "you," "your") and Audiosum ("Company," "we," "us," "our"), governing your access to and use of Audiosum's artificial intelligence-powered audio and video transcription, summarization, and analysis services, accessible through our web platforms audiosum.app and audiosum.ai, mobile applications, APIs, and related services (collectively, the "Service" or "Platform").
          </p>
          <p>
            BY ACCESSING, DOWNLOADING, INSTALLING, OR USING ANY PART OF THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND ALL APPLICABLE LAWS AND REGULATIONS. IF YOU DO NOT AGREE WITH ANY PROVISION OF THESE TERMS, YOU MUST IMMEDIATELY DISCONTINUE USE OF THE SERVICE.
          </p>
        </div>

        <section>
          <h2>1. ACCEPTANCE AND MODIFICATIONS OF TERMS</h2>
          <h3>1.1 Binding Agreement</h3>
          <p>
            Your access to or use of the Service constitutes your acceptance of these Terms, our Privacy Policy (incorporated herein by reference), and any additional terms applicable to specific features or services. This Agreement takes effect immediately upon your first use of the Service.
          </p>
          
          <h3>1.2 Modifications to Terms</h3>
          <p>We reserve the right to modify, update, or replace these Terms at any time at our sole discretion. Material changes will be communicated through:</p>
          <ul>
            <li>Email notification to registered users (if applicable)</li>
            <li>Prominent notice on our website</li>
            <li>In-app notifications</li>
            <li>Updated "Last Modified" date on this document</li>
          </ul>
          <p>Your continued use of the Service after any such changes constitutes your acceptance of the modified Terms. It is your responsibility to review these Terms periodically for updates.</p>

          <h3>1.3 Additional Terms</h3>
          <p>Certain features or services may be subject to additional terms and conditions, which will be presented to you at the time of access. Such additional terms are incorporated into this Agreement by reference.</p>
        </section>

        <section>
          <h2>2. ELIGIBILITY AND ACCOUNT REGISTRATION</h2>
          <h3>2.1 Age Requirements</h3>
          <p>
            The Service is intended for users who are at least 13 years of age. If you are between 13 and 18 years old (or the age of majority in your jurisdiction), you may only use the Service with the involvement and consent of a parent or legal guardian. Users under 13 are strictly prohibited from using the Service.
          </p>
          
          <h3>2.2 Account Registration</h3>
          <ul>
            <li>Account creation through email, Google OAuth, or Apple Sign-In is optional but required for tracking usage limits, managing subscriptions, and accessing certain features</li>
            <li>You must provide accurate, current, and complete information during registration</li>
            <li>You are solely responsible for maintaining the confidentiality and security of your account credentials</li>
            <li>You must immediately notify us of any unauthorized access or security breach</li>
            <li>You are fully responsible for all activities that occur under your account</li>
          </ul>

          <h3>2.3 Account Termination Rights</h3>
          <p>We reserve the right to suspend, disable, or terminate your account at any time for:</p>
          <ul>
            <li>Violation of these Terms</li>
            <li>Suspected fraudulent, abusive, or illegal activity</li>
            <li>Extended periods of inactivity</li>
            <li>Technical or security reasons</li>
            <li>Non-payment of fees (for paid accounts)</li>
          </ul>
        </section>

        <section>
          <h2>3. GRANT OF LICENSE AND PERMITTED USE</h2>
          <h3>3.1 License Grant</h3>
          <p>Subject to your compliance with these Terms, Audiosum grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service for your personal, non-commercial, or internal business purposes in accordance with these Terms.</p>

          <h3>3.2 Permitted Uses</h3>
          <p>You may use the Service to:</p>
          <ul>
            <li>Upload and process audio and video files for transcription and summarization</li>
            <li>Generate AI-powered summaries and analyses of your content</li>
            <li>Access and download your processed content and summaries</li>
            <li>Use our API (if applicable) subject to separate API terms</li>
            <li>Integrate with third-party applications as explicitly permitted</li>
          </ul>

          <h3>3.3 Prohibited Activities</h3>
          <p>You expressly agree NOT to:</p>
          
          <h4>3.3.1 Technical Restrictions</h4>
          <ul>
            <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Service</li>
            <li>Use automated scripts, bots, or other automated means to access the Service</li>
            <li>Attempt to gain unauthorized access to our systems, networks, or user accounts</li>
            <li>Interfere with or disrupt the Service's operation, servers, or networks</li>
            <li>Circumvent any security measures or access controls</li>
            <li>Use the Service to develop competing products or services</li>
          </ul>

          <h4>3.3.2 Content Restrictions</h4>
          <ul>
            <li>Upload content that infringes intellectual property rights of third parties</li>
            <li>Upload content containing malware, viruses, or malicious code</li>
            <li>Upload content that is illegal, harmful, threatening, abusive, or objectionable</li>
            <li>Upload content containing personally identifiable information of third parties without consent</li>
            <li>Upload copyrighted material without proper authorization</li>
            <li>Upload content that violates export control laws or regulations</li>
          </ul>

          <h4>3.3.3 Usage Restrictions</h4>
          <ul>
            <li>Exceed usage limits or quotas assigned to your account</li>
            <li>Use the Service for illegal, fraudulent, or harmful activities</li>
            <li>Resell, redistribute, or sublicense the Service without explicit written permission</li>
            <li>Use the Service to compete with Audiosum or develop similar services</li>
            <li>Violate any applicable laws or regulations in connection with your use</li>
          </ul>
        </section>

        <section>
          <h2>4. USER CONTENT AND INTELLECTUAL PROPERTY</h2>
          <h3>4.1 Your Content Ownership</h3>
          <p>You retain all rights, title, and interest in and to any audio, video, or other content you upload to the Service ("User Content"). We do not claim ownership of your User Content.</p>

          <h3>4.2 License to Process Content</h3>
          <p>By uploading User Content, you grant Audiosum a worldwide, non-exclusive, royalty-free license to:</p>
          <ul>
            <li>Access, store, and process your User Content solely to provide the Service</li>
            <li>Generate transcriptions, summaries, and analyses based on your User Content</li>
            <li>Temporarily cache and store processed results</li>
            <li>Use aggregated, anonymized data for service improvement (with no personally identifiable information)</li>
          </ul>

          <h3>4.3 Content Responsibilities</h3>
          <p>You represent and warrant that:</p>
          <ul>
            <li>You own or have all necessary rights to upload and process your User Content</li>
            <li>Your User Content does not violate any third-party rights or applicable laws</li>
            <li>You have obtained all necessary consents for processing personal data in your User Content</li>
            <li>Your use of the Service complies with all applicable privacy and data protection laws</li>
          </ul>

          <h3>4.4 Content Removal</h3>
          <p>We reserve the right to remove or disable access to any User Content that:</p>
          <ul>
            <li>Violates these Terms</li>
            <li>Infringes third-party rights</li>
            <li>Is reported as inappropriate or illegal</li>
            <li>Poses security or technical risks to our Service</li>
          </ul>

          <h3>4.5 Audiosum Intellectual Property</h3>
          <p>The Service, including all software, algorithms, designs, text, graphics, logos, and other materials, is owned by Audiosum and protected by intellectual property laws. You may not use our intellectual property except as expressly permitted in these Terms.</p>
        </section>

        <section>
          <h2>5. SUBSCRIPTION PLANS, BILLING, AND PAYMENTS</h2>
          <h3>5.1 Service Tiers</h3>
          <p>Audiosum operates on a freemium model with the following tiers:</p>
          <ul>
            <li>Free Trial: 7-day free trial period; a valid payment method is required at sign-up. After the trial ends, your account is automatically upgraded to the Starter tier at $6.99/month</li>
            <li>Pro Plan: $14.99/month (monthly billing) with enhanced features and higher usage limits</li>
            <li>Premium Plan: $28.99/month (monthly billing) with premium features and maximum usage limits</li>
            <li>Annual Plans: Available with 17% discount compared to monthly billing</li>
          </ul>

          <h3>5.2 Billing Terms</h3>
          <ul>
            <li>All fees are charged in advance on a recurring basis (monthly or annually)</li>
            <li>Billing cycles begin on the date of your initial subscription purchase</li>
            <li>Auto-renewal is enabled by default; you may disable it in your account settings</li>
            <li>Price changes will be communicated 30 days in advance for existing subscribers</li>
            <li>All payments are processed in USD unless otherwise specified</li>
          </ul>

          <h3>5.3 Payment Processing</h3>
          <ul>
            <li>Payments are processed through Stripe, a PCI DSS compliant third-party payment processor</li>
            <li>You must provide valid, current payment information</li>
            <li>You authorize us to charge your selected payment method for all applicable fees</li>
            <li>Failed payments may result in service suspension after a grace period</li>
            <li>We are not liable for any fees, charges, or costs imposed by your financial institution</li>
          </ul>

          <h3>5.4 Usage Limits and Overages</h3>
          <ul>
            <li>Each plan includes specific usage limits (processing time, file size, number of files)</li>
            <li>Exceeding usage limits may result in additional charges or service limitations</li>
            <li>Usage resets at the beginning of each billing cycle</li>
            <li>Enterprise plans with custom usage limits are available upon request</li>
          </ul>

          <h3>5.5 REFUND AND CANCELLATION POLICY</h3>
          <h4>5.5.1 Non-Refundable Nature</h4>
          <p>ALL SUBSCRIPTION FEES ARE NON-REFUNDABLE except as expressly set forth below. This no-refund policy applies regardless of whether you use the Service during your subscription period.</p>

          <h4>5.5.2 Cancellation Rights</h4>
          <ul>
            <li>You may cancel your subscription at any time through your account settings</li>
            <li>Cancellations take effect at the end of your current billing period</li>
            <li>You will retain access to paid features until the end of your paid period</li>
            <li>No partial refunds are provided for unused portions of billing periods</li>
            <li>Upon cancellation, your account will automatically downgrade to the free tier</li>
          </ul>

          <h4>5.5.3 Limited Refund Exceptions</h4>
          <p>Refunds may be considered solely at our discretion for:</p>
          <ul>
            <li>Billing errors or duplicate charges</li>
            <li>Technical issues preventing service access for more than 48 consecutive hours</li>
            <li>Fraudulent or unauthorized charges</li>
            <li>Violations of applicable consumer protection laws</li>
          </ul>

          <h4>5.5.4 Refund Request Process</h4>
          <ul>
            <li>All refund requests must be submitted within 48 hours of the billing issue</li>
            <li>Requests must be sent to support@audiosum.app with detailed explanation</li>
            <li>We reserve the right to investigate and verify refund claims</li>
            <li>Processing approved refunds may take 5-10 business days</li>
          </ul>

          <h3>5.6 Price Changes</h3>
          <p>We reserve the right to modify subscription prices with 30 days' advance notice. Price changes will not affect existing subscribers until their next renewal period.</p>
        </section>

        <section>
          <h2>6. DATA SECURITY AND PRIVACY</h2>
          <h3>6.1 Data Protection Commitment</h3>
          <p>We implement industry-standard security measures to protect your data, including encryption in transit and at rest, secure data centers, and regular security audits.</p>

          <h3>6.2 Privacy Policy Integration</h3>
          <p>Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.</p>

          <h3>6.3 Data Processing Location</h3>
          <p>Your data may be processed and stored in various locations worldwide, including within the European Economic Area (EEA), with appropriate safeguards in place.</p>
        </section>

        <section>
          <h2>7. SERVICE AVAILABILITY AND MODIFICATIONS</h2>
          <h3>7.1 Service Availability</h3>
          <p>While we strive to maintain continuous service availability, we do not guarantee uninterrupted access. The Service may experience downtime for maintenance, updates, or unforeseen technical issues.</p>

          <h3>7.2 Service Modifications</h3>
          <p>We reserve the right to modify, update, or discontinue any aspect of the Service at any time, including features, functionality, or technical requirements. We will provide reasonable notice for material changes when feasible.</p>

          <h3>7.3 Maintenance Windows</h3>
          <p>Scheduled maintenance will be communicated in advance when possible. Emergency maintenance may occur without prior notice.</p>
        </section>

        <section>
          <h2>8. TERMINATION</h2>
          <h3>8.1 Termination by You</h3>
          <p>You may terminate this Agreement at any time by:</p>
          <ul>
            <li>Canceling your subscription (if applicable)</li>
            <li>Ceasing use of the Service</li>
            <li>Requesting account deletion through support@audiosum.app</li>
          </ul>

          <h3>8.2 Termination by Audiosum</h3>
          <p>We may terminate or suspend your access immediately, without prior notice, for:</p>
          <ul>
            <li>Material breach of these Terms</li>
            <li>Violation of applicable laws or regulations</li>
            <li>Suspected fraudulent, harmful, or illegal activity</li>
            <li>Non-payment of fees (after grace period)</li>
            <li>Prolonged inactivity (free accounts)</li>
            <li>Technical or security considerations</li>
          </ul>

          <h3>8.3 Effects of Termination</h3>
          <p>Upon termination:</p>
          <ul>
            <li>Your right to access and use the Service immediately ceases</li>
            <li>We may delete your account and associated data after a reasonable retention period</li>
            <li>Accrued payment obligations remain due and payable</li>
            <li>Provisions that by their nature should survive termination will continue in effect</li>
          </ul>
        </section>

        <section>
          <h2>9. DISCLAIMERS AND WARRANTIES</h2>
          <h3>9.1 "AS IS" SERVICE PROVISION</h3>
          <p>THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE.</p>

          <h3>9.2 Disclaimed Warranties</h3>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING:</p>
          <ul>
            <li>Merchantability and fitness for a particular purpose</li>
            <li>Non-infringement of third-party rights</li>
            <li>Accuracy, completeness, or reliability of Service results</li>
            <li>Uninterrupted or error-free operation</li>
            <li>Security of data transmission or storage</li>
            <li>Compatibility with your systems or requirements</li>
          </ul>

          <h3>9.3 AI Technology Limitations</h3>
          <p>You acknowledge that AI-powered features may produce inaccurate, incomplete, or inappropriate results. You are solely responsible for reviewing and verifying all Service outputs before relying on them.</p>
        </section>

        <section>
          <h2>10. LIMITATION OF LIABILITY</h2>
          <h3>10.1 Damages Limitation</h3>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUDIOSUM'S TOTAL LIABILITY ARISING FROM OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF:</p>
          <ul>
            <li>The total amount paid by you to Audiosum in the 12 months preceding the claim</li>
            <li>One hundred U.S. dollars ($100)</li>
          </ul>

          <h3>10.2 Excluded Damages</h3>
          <p>IN NO EVENT SHALL AUDIOSUM BE LIABLE FOR:</p>
          <ul>
            <li>Indirect, incidental, special, exemplary, or consequential damages</li>
            <li>Loss of profits, revenues, data, use, goodwill, or other intangible losses</li>
            <li>Damages resulting from unauthorized access to or alteration of your data</li>
            <li>Damages resulting from third-party conduct or content</li>
            <li>Business interruption or system failures</li>
          </ul>

          <h3>10.3 Essential Purpose</h3>
          <p>These limitations apply even if Audiosum has been advised of the possibility of such damages and notwithstanding any failure of essential purpose of any limited remedy.</p>
        </section>

        <section>
          <h2>11. INDEMNIFICATION</h2>
          <h3>11.1 Your Indemnification Obligations</h3>
          <p>You agree to defend, indemnify, and hold harmless Audiosum, its affiliates, officers, directors, employees, agents, and licensors from and against any claims, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising from or relating to:</p>
          <ul>
            <li>Your use or misuse of the Service</li>
            <li>Your User Content or its processing</li>
            <li>Your violation of these Terms or applicable laws</li>
            <li>Your infringement of third-party rights</li>
            <li>Any false or misleading information provided by you</li>
          </ul>

          <h3>11.2 Indemnification Process</h3>
          <p>We will provide prompt notice of any claim subject to indemnification and cooperate in the defense, provided that our failure to provide prompt notice will not relieve your indemnification obligations except to the extent prejudiced thereby.</p>
        </section>

        <section>
          <h2>12. GOVERNING LAW AND DISPUTE RESOLUTION</h2>
          <h3>12.1 Governing Law</h3>
          <p>These Terms and any disputes arising hereunder shall be governed by and construed in accordance with the laws of Portugal and the European Union, without regard to conflict of law principles.</p>

          <h3>12.2 Jurisdiction and Venue</h3>
          <p>Any legal action or proceeding relating to these Terms shall be instituted in the competent courts of Lisbon, Portugal. You consent to the jurisdiction of such courts and waive any objection to venue therein.</p>

          <h3>12.3 Alternative Dispute Resolution</h3>
          <p>Prior to initiating formal legal proceedings, the parties agree to attempt to resolve disputes through good faith negotiations. If unsuccessful, disputes may be resolved through binding arbitration administered by a recognized arbitration institution, conducted in English or Portuguese.</p>

          <h3>12.4 Class Action Waiver</h3>
          <p>YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.</p>
        </section>

        <section>
          <h2>13. FORCE MAJEURE</h2>
          <p>Neither party shall be liable for any delay or failure in performance due to events beyond their reasonable control, including but not limited to:</p>
          <ul>
            <li>Natural disasters, pandemics, or acts of God</li>
            <li>Government actions, laws, or regulations</li>
            <li>War, terrorism, or civil unrest</li>
            <li>Internet or telecommunications failures</li>
            <li>Third-party service provider outages</li>
            <li>Cyber attacks or security breaches</li>
          </ul>
          <p>The affected party must provide prompt notice and use reasonable efforts to remedy the situation.</p>
        </section>

        <section>
          <h2>14. COMPLIANCE AND LEGAL REQUIREMENTS</h2>
          <h3>14.1 Export Controls</h3>
          <p>You agree to comply with all applicable export and import laws and regulations. You represent that you are not located in a country subject to embargo or designated as a "terrorist supporting" country.</p>

          <h3>14.2 Regulatory Compliance</h3>
          <p>You are responsible for ensuring your use of the Service complies with all applicable laws, regulations, and industry standards in your jurisdiction.</p>

          <h3>14.3 GDPR and Data Protection</h3>
          <p>If you are located in the European Union, additional rights and protections may apply under the General Data Protection Regulation (GDPR) as outlined in our Privacy Policy.</p>
        </section>

        <section>
          <h2>15. GENERAL PROVISIONS</h2>
          <h3>15.1 Entire Agreement</h3>
          <p>These Terms, together with our Privacy Policy and any additional terms referenced herein, constitute the entire agreement between you and Audiosum regarding the Service and supersede all prior or contemporaneous communications.</p>

          <h3>15.2 Severability</h3>
          <p>If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the remaining Terms remain in full force and effect.</p>

          <h3>15.3 No Waiver</h3>
          <p>Our failure to enforce any provision of these Terms shall not be deemed a waiver of such provision or our right to enforce it in the future.</p>

          <h3>15.4 Assignment</h3>
          <p>You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms without restriction.</p>

          <h3>15.5 Survival</h3>
          <p>Provisions that by their nature should survive termination of these Terms shall survive, including but not limited to intellectual property rights, disclaimers, limitations of liability, and indemnification obligations.</p>

          <h3>15.6 Language</h3>
          <p>These Terms are written in English. Any translated versions are provided for convenience only, and the English version shall control in case of conflict.</p>

          <h3>15.7 Updates and Notifications</h3>
          <p>We may provide notices and updates through email, in-app notifications, or by posting on our website. It is your responsibility to maintain current contact information.</p>
        </section>

        <section className="contact-section">
          <h2>16. CONTACT INFORMATION</h2>
          <p>For questions, concerns, or support regarding these Terms of Service, please contact us:</p>
          <p>Email: <a href="mailto:support@audiosum.app">support@audiosum.app</a></p>
          <p>Website: <a href="https://audiosum.app">https://audiosum.app</a></p>
          <p>Legal Department: Audiosum Legal Team</p>
          <p>Response Time: We aim to respond to all inquiries within 48 hours during business days.</p>
        </section>

        <footer className="terms-footer">
          <p>By using the Audiosum Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</p>
          <p>Last Updated: September 24, 2025</p>
          <p>© 2025 Audiosum. All rights reserved.</p>
        </footer>
      </div>
    );
  };

  return (
    <div className="terms-page">
      <SEOHead 
        title={currentSEO.title}
        description={currentSEO.description}
        keywords={currentSEO.keywords}
      />
      <Nav />
      <main className="terms-main">
        <div className="container">
          {getContent()}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsConditions;